import React, { PropsWithChildren, ReactNode } from "react";
import ReactDOM from "react-dom/client";
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    RouterProvider,
    useNavigation,
} from "react-router-dom";
import { HomePage } from "./pages/AdminPages/HomePage/HomePage";
import Layout from "./components/Layout/Layout";
import { LoginPage } from "./pages/StaticPages/LoginPage/LoginPage";
import { HistoryTransactions } from "./pages/AdminPages/HistoryTransactions/HistoryTransactions";
import "./assets/css/global.css";
import { createTheme, ThemeProvider } from "@mui/material";
import AuthProvider from "./providers/AuthProvider";
import { Profile } from "./pages/AdminPages/Profile/Profile";
import { MetersPage } from "./pages/AdminPages/MetersPage/MetersPage";
import { useAuthStore } from "./store/auth.store";
import { MetersHistory } from "./pages/AdminPages/MetersPage/MetersHistory/MetersHistory";
import { NewsPage } from "./pages/AdminPages/NewsPage/NewsPage";
import { HistoryPayments } from "./pages/AdminPages/HistoryPayments/HistoryPayments";
import { HistoryDetailed } from "./pages/AdminPages/HistoryPayments/HistoryDetailed";
import { SnackbarProvider } from "notistack";
import { RegistrationLink } from "./pages/StaticPages/Registration/Registration";
import { RegistrationPassword } from "./pages/StaticPages/Registration/RegistrationPassword";
import { ResetPassword } from "./pages/StaticPages/ResetPassword/ResetPassword";
import { ResetPasswordCode } from "./pages/StaticPages/ResetPassword/ResetPasswordCode";
import { ResetPasswordChange } from "./pages/StaticPages/ResetPassword/ResetPasswordChange";
import { OrganizationsPage } from "./pages/AdminPages/Organizations/OrganizationsPage";
import { TicketsPage } from "./pages/AdminPages/Tickets/TicketsPage";
import { VotingPage } from "./pages/AdminPages/VotingPage/VotingPage";
import "react-photo-view/dist/react-photo-view.css";
import { TicketDetailed } from "./pages/AdminPages/Tickets/TicketDetailed";

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

// Пути страниц
const router = createBrowserRouter([
    {
        index: true,
        element: <LoginPage />,
        path: "/",
    },
    {
        path: "*",
        element: <Navigate to={"/"} />,
    },
    {
        element: <RegistrationLink />,
        path: "/registration",
    },
    {
        element: <RegistrationPassword />,
        path: "/registration/password",
    },
    {
        element: <ResetPassword />,
        path: "/resetpassword",
    },
    {
        element: <ResetPasswordCode />,
        path: "/resetpassword/code",
    },
    {
        element: <ResetPasswordChange />,
        path: "/resetpassword/change",
    },
    {
        path: "/admin",
        element: <AuthProvider />,
        children: [
            {
                path: "/admin",
                element: <Layout />,
                children: [
                    {
                        element: <Navigate to={"/admin/index"} />,
                        path: "/admin/",
                    },
                    {
                        element: <HistoryTransactions />,
                        path: "/admin/transactions",
                    },
                    {
                        element: <HistoryPayments />,
                        path: "/admin/payments",
                    },
                    {
                        element: <HistoryDetailed />,
                        path: "/admin/payments/detailed",
                    },
                    {
                        element: <HomePage />,
                        path: "/admin/index",
                    },
                    {
                        element: <Profile />,
                        path: "/admin/profile",
                    },
                    {
                        element: <MetersPage />,
                        path: "/admin/meters",
                    },
                    {
                        element: <VotingPage />,
                        path: "/admin/meetings",
                    },
                    {
                        element: <MetersHistory />,
                        path: "/admin/meters/history",
                    },
                    {
                        element: <NewsPage />,
                        path: "/admin/news",
                    },
                    {
                        element: <OrganizationsPage />,
                        path: "/admin/organizations",
                    },
                    {
                        element: <TicketsPage />,
                        path: "/admin/tickets",
                    },
                    {
                        element: <TicketDetailed />,
                        path: "/admin/tickets/detailed",
                    },
                ],
            },
        ],
    },
]);

// Настройка темы
const theme = createTheme({
    palette: {
        // Основная цветовая палитра
        primary: {
            main: "#0E74B4",
        },
        secondary: {
            main: "#eafaf6",
        },
        info: {
            main: "#eafaf6",
        },
        success: {
            main: "#0E74B4",
        },
        error: {
            main: "#FF8674",
        },

        text: {
            secondary: "#82809c",
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                // глобальный цвет текста
                root: {
                    color: "#0E74B4",
                },
                contained: {
                    color: "#fff",
                },
                outlined: {
                    color: "#0E74B4",
                },
            },
        },
    },
});

// Обработчик, получающий fingerprint и userAgent пользователя
const IndexProvider = ({ children }: any) => {
    const { setUserBrowserData } = useAuthStore();

    React.useLayoutEffect(() => {
        setUserBrowserData(navigator.userAgent);
    }, []);

    return children;
};

// Рендер всей страницы
root.render(
    <IndexProvider>
        <ThemeProvider theme={theme}>
            <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
                <RouterProvider router={router} />
            </SnackbarProvider>
        </ThemeProvider>
    </IndexProvider>,
);
